import { useCallback, useEffect, useState } from "react";
import {
  notificationApi,
  type NotificationPreferences,
  type NotificationSummary,
  DEFAULT_NOTIFICATION_PREFERENCES,
} from "@/lib/api/notifications";

let sharedSummary: NotificationSummary | null = null;
let sharedPrefs: NotificationPreferences = DEFAULT_NOTIFICATION_PREFERENCES;
let summaryListeners = new Set<(s: NotificationSummary | null) => void>();
let prefsListeners = new Set<(p: NotificationPreferences) => void>();
let summaryInflight: Promise<void> | null = null;
let prefsInflight: Promise<void> | null = null;

function publishSummary(summary: NotificationSummary | null) {
  sharedSummary = summary;
  summaryListeners.forEach((fn) => fn(summary));
}

function publishPrefs(prefs: NotificationPreferences) {
  sharedPrefs = prefs;
  prefsListeners.forEach((fn) => fn(prefs));
}

async function fetchSummary() {
  if (summaryInflight) return summaryInflight;
  summaryInflight = (async () => {
    try {
      const res = await notificationApi.summary();
      publishSummary(res.data.summary);
    } catch {
      /* best-effort */
    } finally {
      summaryInflight = null;
    }
  })();
  return summaryInflight;
}

async function fetchPrefs() {
  if (prefsInflight) return prefsInflight;
  prefsInflight = (async () => {
    try {
      const res = await notificationApi.getPreferences();
      publishPrefs(res.data.preferences);
    } catch {
      /* best-effort */
    } finally {
      prefsInflight = null;
    }
  })();
  return prefsInflight;
}

/** Shared notification summary + preferences (polls summary every 60s). */
export function useNotificationSummary() {
  const [summary, setSummary] = useState<NotificationSummary | null>(sharedSummary);
  const [preferences, setPreferences] =
    useState<NotificationPreferences>(sharedPrefs);

  const refresh = useCallback(() => {
    void fetchSummary();
  }, []);

  const refreshPreferences = useCallback(() => {
    void fetchPrefs();
  }, []);

  const savePreferences = useCallback(async (patch: Partial<NotificationPreferences>) => {
    const res = await notificationApi.updatePreferences(patch);
    publishPrefs(res.data.preferences);
    await fetchSummary();
    return res.data.preferences;
  }, []);

  useEffect(() => {
    summaryListeners.add(setSummary);
    prefsListeners.add(setPreferences);
    void fetchSummary();
    void fetchPrefs();
    const id = window.setInterval(() => void fetchSummary(), 60_000);
    return () => {
      summaryListeners.delete(setSummary);
      prefsListeners.delete(setPreferences);
      window.clearInterval(id);
    };
  }, []);

  return {
    summary,
    preferences,
    refresh,
    refreshPreferences,
    savePreferences,
  };
}

export function getCachedNotificationPreferences(): NotificationPreferences {
  return sharedPrefs;
}
